import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import AppDocTabs, { AppNotFound } from "../components/AppDocTabs";
import { getApp } from "../data/apps";
import "../styles/docs.css";

const TITLE = { privacy: "개인정보처리방침", terms: "이용약관" };
const num = (i: number) => String(i + 1).padStart(2, "0");
const dot = (d: string) => d.replace(/-/g, ".");
// 번호는 화면이 붙이므로 데이터 제목 앞의 "1. " 같은 번호는 뗀다
const bare = (t: string) => t.replace(/^\d+\.\s*/, "");

export default function PolicyPage({ kind }: { kind: "privacy" | "terms" }) {
  const { slug = "" } = useParams();
  const app = getApp(slug);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    if (app) document.title = `${app.name} ${TITLE[kind]} | kwangbok.me`;
  }, [app, kind]);

  if (!app) return <AppNotFound slug={slug} />;
  const doc = app.policy[kind];

  return (
    <SiteLayout crumb={`apps / ${app.slug} / ${kind}`}>
      <div className="docs">
        <AppDocTabs app={app} current={kind} />
        <div className="docs-split docs-split--toc">
          <nav aria-label="목차" className={`docs-card docs-toc${tocOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="docs-toc-toggle"
              aria-expanded={tocOpen}
              aria-controls="docs-toc-list"
              onClick={() => setTocOpen((o) => !o)}
            >
              <span>목차</span>
              <span className="docs-muted">{doc.sections.length}</span>
            </button>
            <span className="docs-toc-label docs-mono docs-muted">목차</span>
            <ol id="docs-toc-list" className="docs-toc-list">
              {doc.sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="docs-num">{num(i)}</span>
                    {bare(s.title)}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="docs-card docs-doc">
            <h1 className="docs-h1">{TITLE[kind]}</h1>
            <div className="docs-meta docs-mono docs-muted">
              <span>
                effective <time dateTime={doc.effective_at}>{dot(doc.effective_at)}</time>
              </span>
            </div>
            {doc.previous && doc.previous.length > 0 && (
              <div className="docs-prev">
                <span className="docs-mono docs-muted">이전 버전</span>
                <ul>
                  {doc.previous.map((p) => (
                    <li key={p.effective_at}>
                      <time className="docs-mono" dateTime={p.effective_at}>
                        {dot(p.effective_at)}
                      </time>{" "}
                      {p.href ? (
                        <a href={p.href} className="docs-link">
                          {p.note}
                        </a>
                      ) : (
                        p.note
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {kind === "privacy" && (
              <p className="docs-lead">
                {app.operator.name}(이하 "운영자")는 {app.name} 이용자의 개인정보를 「개인정보 보호법」에
                따라 처리합니다. 이 방침은 「개인정보 보호법」 제30조에 따라 작성했습니다.
              </p>
            )}
            {doc.sections.map((s, i) => (
              <section key={s.id} id={s.id} className="docs-section">
                <span className="docs-num">{num(i)}</span>
                <div className="docs-section-body">
                  <h2>{bare(s.title)}</h2>
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                  {s.table && (
                    <div className="docs-table-wrap">
                      <table className="docs-table">
                        <thead>
                          <tr>
                            {s.table.head.map((h) => (
                              <th key={h} scope="col">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((r, k) => (
                            <tr key={k}>
                              {r.map((c, m) => (
                                <td key={m}>{c}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            ))}
          </article>
        </div>
      </div>
    </SiteLayout>
  );
}
